import { Pin } from 'lucide-react'

import { Card } from '@modules/shadcn/ui/Card/Card'
import { formatTimestamp } from '@modules/utils/dateUtils'

import type { PinnedHistoryItem } from '../hooks/useHistoryFilter'
import { AI_COLOR_PIN_LIMIT, resolveHistoryTitle } from '../utils/historyDisplay'

import { AiImageCard } from './AiImageCard'

interface PinnedHistoryPanelProps {
  items: PinnedHistoryItem[]
  onSelect: (item: PinnedHistoryItem) => void
  onUnpin: (imageId: number) => void
  onToggleFavorite?: (imageId: number, favorited: boolean) => Promise<void> | void
  onSaveTitle?: (imageId: number, title: string) => Promise<void> | void
}

/**
 * 企業画面の履歴エリア先頭に置くピン留めパネル（決定事項2。モックの「ピン留め3 / 4」）。
 * 1件以上あるときだけ描画する（design.md D8）。カードにはカット名を出し、削除ボタンは出さない。
 */
export const PinnedHistoryPanel: React.FC<PinnedHistoryPanelProps> = ({
  items,
  onSelect,
  onUnpin,
  onToggleFavorite,
  onSaveTitle,
}) => {
  if (items.length === 0) return null
  const remaining = AI_COLOR_PIN_LIMIT - items.length

  return (
    <Card
      className="border border-orange-200 bg-orange-50/40 p-6 shadow-sm"
      data-testid="pinned-history-panel"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <Pin aria-hidden className="h-4 w-4 fill-orange-600 text-orange-600" />
            ピン留め{' '}
            <span className="font-normal text-muted-foreground">
              {items.length} / {AI_COLOR_PIN_LIMIT}
            </span>
          </h3>
          <span className="text-xs text-muted-foreground">
            施主画面でも「ピン留め」として先頭に表示されます
          </span>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-1">
          {items.map(({ item, cutLabel }) => (
            <AiImageCard
              categoryTag={item.categoryTag}
              createdAt={item.createdAt ? formatTimestamp(item.createdAt) : undefined}
              createdBy={item.createdBy}
              cutLabel={cutLabel}
              disableDelete
              idPrefix={`pinned-${item.imageId}`}
              isLiked={item.favorited}
              isPinned
              isSelected={item.isActive}
              key={item.key}
              onSaveTitle={
                onSaveTitle && item.imageId ? title => onSaveTitle(item.imageId!, title) : undefined
              }
              onSelect={() => onSelect({ item, cutLabel, pinnedAt: item.pinnedAt! })}
              onToggleLike={
                onToggleFavorite && item.imageId
                  ? () => onToggleFavorite(item.imageId!, !item.favorited)
                  : undefined
              }
              onTogglePin={item.imageId ? () => onUnpin(item.imageId!) : undefined}
              resolvedTitle={resolveHistoryTitle(item)}
              title={item.title}
              url={item.url}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {remaining > 0
            ? `あと${remaining}件 ピン留めできます`
            : `ピン留めは${AI_COLOR_PIN_LIMIT}件までです`}
        </p>
      </div>
    </Card>
  )
}
